import cn from 'classnames';
import throttle from 'lodash/throttle';
import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import Button from 'components/common/Button';
import DiscountTag from 'components/common/DiscountTag';
import StrikethroughSkeleton from 'components/common/StrikethroughSkeleton';
import Text from 'components/common/Text';
import { UserContext, VoucherContext } from 'components/signUpFunnel/Contexts';
import FreeForLifeBanner from 'components/signUpFunnel/FreeForLifeBanner';
import MaxDiscountMessage from 'components/signUpFunnel/shared/MaxDiscountMessage';
import { HOME_CHEF, TEMPO } from 'context/BrandContext';
import { hasServingRange } from 'helpers/servings';
import useBrand from 'hooks/useBrand';
import { KETO } from 'lib/constants';
import styles from 'styles/components/signUpFunnel/box/OrderSummaryMobile';

import FirstWeekDisclaimer from './FirstWeekDisclaimer';
import MobileOrderSummaryHeader from './MobileOrderSummaryHeader';
import PricePerServing from './PricePerServing';
import TempoOrderSummaryPPS from './TempoOrderSummaryPPS';

const MIN_HEIGHT = 124;
const TRANSITION_HEIGHT = 150;
const TEMPO_MAX_HEIGHT = 375;
const HOME_CHEF_MAX_HEIGHT = 485;

const OrderSummaryMobile = ({
  box,
  handleContinue,
  isSubmitting,
  servings,
  weeklyMeals,
  error = '',
}) => {
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [moving, setMoving] = useState(false);
  const [sliderHeight, setSliderHeight] = useState(MIN_HEIGHT);
  const [includesServingRange, setIncludesServingRange] = useState(hasServingRange(box));

  const isHomeChef = useBrand(HOME_CHEF);
  const isTempo = useBrand(TEMPO);

  const { user } = useContext(UserContext);
  const { voucher } = useContext(VoucherContext);

  const buttonRef = useRef();
  const errorRef = useRef();

  const SLIDER_MAX_HEIGHT =
    (isHomeChef && includesServingRange) || (isHomeChef && !userHasKetoOptIn)
      ? HOME_CHEF_MAX_HEIGHT
      : TEMPO_MAX_HEIGHT;

  const userHasKetoOptIn = useMemo(() => {
    return user?.optIns?.includes(KETO);
  }, [user?.optIns]);

  useEffect(() => {
    if (summaryOpen) {
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('sign_up_box_open');
    } else {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('sign_up_box_open');
    }
  }, [summaryOpen]);

  useEffect(() => {
    if (sliderHeight >= TRANSITION_HEIGHT) {
      setSummaryOpen(true);
    } else if (sliderHeight < TRANSITION_HEIGHT) {
      setSummaryOpen(false);
    }
  }, [sliderHeight]);

  useEffect(() => {
    document.body.classList.add('sign_up_box');

    return () => {
      document.body.classList.remove('sign_up_box');
    };
  }, []);

  useEffect(() => {
    setIncludesServingRange(hasServingRange(box));
  }, [box]);

  const throttlePointerUp = throttle(e => (handlePointerUp(e), 50));
  const throttlePointerMove = throttle(e => (handlePointerMove(e), 50));

  const handlePointerDown = e => {
    e.stopPropagation();
    e.preventDefault();
    setMoving(true);
    document.addEventListener('pointermove', throttlePointerMove);
    document.addEventListener('pointerup', throttlePointerUp);
  };

  const handlePointerMove = e => {
    setSummaryOpen(true);
    setMoving(true);
    const slideUpHeight = window.innerHeight - e.clientY;
    if (slideUpHeight <= SLIDER_MAX_HEIGHT && slideUpHeight > MIN_HEIGHT) {
      setSliderHeight(slideUpHeight);
    }
  };

  const handlePointerUp = () => {
    const isOpen = !summaryOpen;
    setSummaryOpen(isOpen);
    setMoving(false);
    if (isOpen) {
      setSliderHeight(SLIDER_MAX_HEIGHT);
    } else {
      setSliderHeight(MIN_HEIGHT);
    }

    document.removeEventListener('pointermove', throttlePointerMove);
    document.removeEventListener('pointerup', throttlePointerUp);
  };

  const handleClose = e => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setSummaryOpen(false);
    setSliderHeight(MIN_HEIGHT);
  };

  const hasSmsDiscount = user?.smsDiscount && box?.smsIncentivePromo;

  const defaultPlanDetails = (
    <>
      {isHomeChef && `${servings} Servings X ${weeklyMeals} Recipes`}
      {isTempo &&
        (voucher?.formattedDiscountString
          ? `${voucher?.formattedDiscountString} off your first box`
          : `${weeklyMeals} meals`)}
    </>
  );

  return (
    <div className="h-0 bp2:hidden">
      {summaryOpen ? (
        <div
          className={cn(
            styles.widthFillAvailable,
            styles.shadow,
            'fixed bottom-0 right-0 cursor-grabbing touch-none rounded-t-2xl bg-white-100 px-6 py-4'
          )}
          style={{ height: sliderHeight + 'px', display: moving && 'inline' }}
          onPointerDown={handlePointerDown}
        >
          <div className="flex justify-between rounded-t-2xl pb-4 text-left">
            <MobileOrderSummaryHeader box={box} defaultPlanDetails={defaultPlanDetails} />
          </div>
          <div className="mb-4 flex w-full gap-2">
            <Button
              variation="PillOutline"
              onClick={handleClose}
              onPointerDown={e => e.stopPropagation()}
              className="flex-1"
            >
              Close
            </Button>
            <Button
              variation="Pill"
              onClick={handleContinue}
              onPointerDown={e => e.stopPropagation()}
              isSubmitting={isSubmitting}
              className="flex-1"
            >
              Continue
            </Button>
          </div>
          {isHomeChef && <PricePerServing box={box} includesServingRange={includesServingRange} />}
          <Text
            htmlElement="dl"
            className={cn('flex flex-col border-solid border-charcoal-60 text-left', {
              'border-b': !userHasKetoOptIn && isHomeChef,
            })}
          >
            {isHomeChef ? (
              <>
                <div className="flex">
                  <Text
                    className="flex-1 pb-2"
                    htmlElement="dt"
                    brandStylesOverrides="text text-secondary"
                  >
                    Meals Per Week
                  </Text>
                  <Text htmlElement="dd" brandStylesOverrides="text text-secondary">
                    {weeklyMeals}
                  </Text>
                </div>
                <div className="flex">
                  <Text
                    className="flex-1 pb-2"
                    htmlElement="dt"
                    brandStylesOverrides="text text-secondary"
                  >
                    Servings Per Meal
                  </Text>
                  <Text className="pb-2" htmlElement="dd" brandStylesOverrides="text-secondary">
                    {servings}
                  </Text>
                </div>
              </>
            ) : (
              <TempoOrderSummaryPPS box={box} />
            )}
            {!isTempo && (
              <Text brandStylesOverrides="textSm text-charcoal-90" className="pb-2">
                You can change this at any time
              </Text>
            )}
          </Text>
          {!userHasKetoOptIn && (
            <>
              <Text brandStylesOverrides="text textMd" className="my-2">
                Total
              </Text>
              <Text htmlElement="dl" className="text-left">
                <div className="flex">
                  <Text
                    className="mb-2 flex-1"
                    brandStylesOverrides="text text-secondary"
                    htmlElement="dt"
                  >
                    Subtotal
                  </Text>
                  <Text htmlElement="dd" className="flex">
                    {box?.promoApplied &&
                    box?.subtotalFormatted !== box?.promo.subtotalFormatted ? (
                      <>
                        {hasSmsDiscount && <DiscountTag discountText="5% off" />}
                        <StrikethroughSkeleton
                          strikethroughText={box?.subtotalFormatted}
                          textDisplayed={box?.promo.subtotalFormatted}
                          ariaLabel="subtotal"
                        />
                      </>
                    ) : hasSmsDiscount ? (
                      <>
                        <DiscountTag discountText="5% off" />
                        <StrikethroughSkeleton
                          strikethroughText={box?.subtotalFormatted}
                          textDisplayed={box?.smsIncentivePromo.subtotalFormatted}
                          ariaLabel="subtotal"
                        />
                      </>
                    ) : (
                      <Text htmlElement="span" brandStylesOverrides="textMd text-secondary">
                        {box?.subtotalFormatted}
                      </Text>
                    )}
                  </Text>
                </div>

                <MaxDiscountMessage maxDiscountMessage={box?.promo?.maxDiscountMessage} />
                <div className="flex">
                  <Text
                    brandStylesOverrides="text text-secondary"
                    className="flex-1 pb-2"
                    htmlElement="dt"
                  >
                    Shipping
                  </Text>
                  <Text htmlElement="dd">
                    {box?.shippingPromoApplied ? (
                      <StrikethroughSkeleton
                        strikethroughText={box?.promo.shippingPromo.estimatedShippingFormatted}
                        textDisplayed={box?.promo.shippingPromo.shippingPromoText}
                        ariaLabel="shipping"
                      />
                    ) : (
                      <Text htmlElement="span" brandStylesOverrides="textMd text-secondary">
                        {box?.shippingFormatted}
                      </Text>
                    )}
                  </Text>
                </div>
              </Text>
            </>
          )}
          {voucher && voucher.freeForLifeDiscounts && voucher.freeForLifeDiscounts.length > 0 && (
            <div className="mb-4 flex flex-col gap-2">
              {voucher.freeForLifeDiscounts.map((discount, index) => {
                return <FreeForLifeBanner text={discount.bannerText} key={index} />;
              })}
            </div>
          )}
          <FirstWeekDisclaimer box={box} includesServingRange={includesServingRange} />
        </div>
      ) : (
        <div
          className={cn(
            styles.widthFillAvailable,
            styles.shadow,
            'fixed bottom-0 right-0 flex cursor-grabbing touch-none flex-col rounded-t-3xl bg-white-100 p-4'
          )}
          style={{ height: sliderHeight + 'px' }}
        >
          <div onPointerDown={handlePointerDown} className="flex-grow touch-none">
            <CSSTransition
              in={Boolean(error)}
              classNames="fadeInOut"
              unmountOnExit
              timeout={200}
              nodeRef={errorRef}
            >
              <div className="py-2" ref={errorRef}>
                <Text brandStylesOverrides="textXs text-negative">{error}</Text>
              </div>
            </CSSTransition>
            <Text htmlElement="span">
              <div className="mb-3 flex w-full items-center justify-between">
                <MobileOrderSummaryHeader box={box} defaultPlanDetails={defaultPlanDetails} />
              </div>
            </Text>
          </div>
          <CSSTransition
            in={!moving}
            classNames="fadeInOut"
            unmountOnExit
            timeout={200}
            nodeRef={buttonRef}
          >
            <div className="w-full" ref={buttonRef}>
              <Button
                type="submit"
                onClick={handleContinue}
                isSubmitting={isSubmitting}
                variation="Pill"
                className="w-full"
              >
                Continue
              </Button>
            </div>
          </CSSTransition>
        </div>
      )}
    </div>
  );
};

OrderSummaryMobile.propTypes = {
  servings: PropTypes.number.isRequired,
  weeklyMeals: PropTypes.number.isRequired,
  handleContinue: PropTypes.func.isRequired,
  box: PropTypes.shape({
    subtotalFormatted: PropTypes.string.isRequired,
    subtotalCents: PropTypes.number.isRequired,
    totalFormatted: PropTypes.string.isRequired,
    totalCents: PropTypes.number.isRequired,
    promoApplied: PropTypes.bool.isRequired,
    shippingFormatted: PropTypes.string.isRequired,
    shippingCents: PropTypes.number.isRequired,
    shippingPromoApplied: PropTypes.bool,
    taxFormatted: PropTypes.string.isRequired,
    taxCents: PropTypes.number.isRequired,
    pricePerServingFormatted: PropTypes.string.isRequired,
    pricePerServingCents: PropTypes.number.isRequired,
    smsIncentivePromo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
    }),
    afterSignUpTotalFormatted: PropTypes.string.isRequired,
    afterSignUpTotalCents: PropTypes.number.isRequired,
    promo: PropTypes.shape({
      subtotalCents: PropTypes.number,
      subtotalFormatted: PropTypes.string,
      pricePerServingFormatted: PropTypes.string,
      shippingPromo: PropTypes.shape({
        estimatedShippingFormatted: PropTypes.string,
        shippingPromoText: PropTypes.string,
      }),
      maxDiscountMessage: PropTypes.string,
    }),
  }),
  isSubmitting: PropTypes.bool.isRequired,
  error: PropTypes.string,
};

export default OrderSummaryMobile;
